import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { cacheService } from '../services/cache/index';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Clientes_SaveInput.ts';
import '../contracts/Clientes_UpdateInput.ts';
import '../contracts/Clientes_CarregaClientesResponse';
import '../contracts/Clientes_DocumentConfigInput';
import '../contracts/Clientes_DocumentConfigResponse';
import '../contracts/Clientes_GetCustomerByIdResponse';
import '../contracts/Clientes_SaveResponse';
import '../contracts/Clientes_UpdateResponse';

import '../contracts/ConsultaCep_ConsultaResponse';
import '../contracts/GestaoClientes_CarregaInfosGraficoResumoResponse';
import '../contracts/GestaoClientes_CarregaInfosGraficoDiarioResponse';
import '../contracts/GestaoClientes_CarregaInfosGraficoOrcamentosSemCompromissoMesAMesResponse';
import '../contracts/Origens_BuscaOrigensResponse';
import type { Clientes_CarregaClientesRow } from '../contracts/Clientes_CarregaClientesRow';
import type { Clientes_SaveInput } from '../contracts/Clientes_SaveInput';
import type { Clientes_UpdateInput } from '../contracts/Clientes_UpdateInput';
/**
 * Composable for Cliente (Customer) operations
 * Provides methods to interact with the Clientes API
 */
/**
 * A refused save. duplicate_document carries the client that already holds the
 * document in `dados.existing_cliente`, which the form needs to name it.
 */
function clienteSaveError(
  response: { error?: string | null; dados?: unknown },
  fallback: string,
): Error & { existingCliente?: { cliente_id: number; cliente_nome: string } } {
  const dados = response.dados as { existing_cliente?: { cliente_id: number; cliente_nome: string } } | null | undefined;
  return Object.assign(new Error(response.error || fallback), { existingCliente: dados?.existing_cliente });
}

export function useCliente() {
  const isLoading = ref(false);
  const error = ref(null);

  // Initialize date formatter at top level (Typed Data Layer)
  const { fromApiDateTimeFormat, toApiDateTimeFormat } = useDateFormatter();

  /**
   * Fetch single cliente by ID
   * Converts datetime fields from ISO 8601 UTC to Date objects (Typed Data Layer)
   */
  const fetchCliente = async (clienteId: number) => {
    log('useCliente', 'fetchCliente', 'Fetching cliente', clienteId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/clientes/getCustomerByID/${clienteId}`, {
        responseContract: 'Clientes_GetCustomerByIdResponse',
      });
      log('useCliente', 'fetchCliente', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Erro ao buscar cliente');
        }
        const data = response.dados;

        // Convert datetime fields: ISO 8601 string → Date object (Typed Data Layer)
        return {
          ...data,
          cliente_data_cadastro: fromApiDateTimeFormat(data.cliente_data_cadastro ?? null),
          cliente_data_ultimo_orcamento: fromApiDateTimeFormat(data.cliente_data_ultimo_orcamento ?? null),
          cliente_data_ultimo_compromisso: fromApiDateTimeFormat(data.cliente_data_ultimo_compromisso ?? null),
          cliente_data_ultimo_contato_fidelizacao: fromApiDateTimeFormat(data.cliente_data_ultimo_contato_fidelizacao ?? null),
        };
      } else {
        throw new Error(response.error || 'Erro ao buscar cliente');
      }
    } catch (err) {
      log('useCliente', 'fetchCliente', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new cliente
   * Converts datetime fields from Date objects to ISO 8601 UTC (Typed Data Layer)
   */
  const createCliente = async (data: any) => {
    log('useCliente', 'createCliente', 'Creating cliente', data);
    isLoading.value = true;
    error.value = null;

    try {
      // Convert datetime fields: Date object → ISO 8601 string (Typed Data Layer)
      const payload: Clientes_SaveInput = {
        ...data,
        cliente_data_cadastro: toApiDateTimeFormat(data.cliente_data_cadastro),
      };

      validate('Clientes_SaveInput', payload);
      const response = await httpService.post('/index.php/clientes/save', payload, {
        responseContract: 'Clientes_SaveResponse',
      });
      log('useCliente', 'createCliente', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw clienteSaveError(response, 'Erro ao salvar cliente');
      }
    } catch (err) {
      log('useCliente', 'createCliente', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Build the clientes/update body. Converts the datetime field from a Date
   * object to ISO 8601 UTC (Typed Data Layer) — but ONLY when the caller sent
   * one. clientes/update is a partial update: a key that is not sent is not
   * touched, while a blank cliente_data_cadastro reaches the app-api as a
   * filled null and erased the registration date on every archive
   * (ClienteArchiveModal sends {cliente_id, cliente_status} only).
   */
  const toUpdatePayload = (data: any): Clientes_UpdateInput => {
    const { cliente_data_cadastro, ...rest } = data;
    if (cliente_data_cadastro == null) {
      return rest;
    }
    return { ...rest, cliente_data_cadastro: toApiDateTimeFormat(cliente_data_cadastro) };
  };

  /**
   * Update existing cliente
   * Converts datetime fields from Date objects to ISO 8601 UTC (Typed Data Layer)
   */
  const updateCliente = async (data: any) => {
    log('useCliente', 'updateCliente', 'Updating cliente', data);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Clientes_UpdateInput = toUpdatePayload(data);

      validate('Clientes_UpdateInput', payload);
      const response = await httpService.post('/index.php/clientes/update', payload, {
        responseContract: 'Clientes_UpdateResponse',
      });
      log('useCliente', 'updateCliente', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw clienteSaveError(response, 'Erro ao atualizar cliente');
      }
    } catch (err) {
      log('useCliente', 'updateCliente', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update cliente with PUT method (used for archive)
   * Converts datetime fields from Date objects to ISO 8601 UTC (Typed Data Layer)
   */
  const updateClientePut = async (data: any) => {
    log('useCliente', 'updateClientePut', 'Updating cliente with PUT', data);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Clientes_UpdateInput = toUpdatePayload(data);

      validate('Clientes_UpdateInput', payload);
      const response = await httpService.put('/index.php/clientes/update', payload, {
        responseContract: 'Clientes_UpdateResponse',
      });
      log('useCliente', 'updateClientePut', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw clienteSaveError(response, 'Erro ao atualizar cliente');
      }
    } catch (err) {
      log('useCliente', 'updateClientePut', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch the full clientes list for the current user's filter scope.
   * Returns row shapes as-is — DataTable handles datetime formatting at render.
   */
  const fetchClientesList = async (): Promise<Clientes_CarregaClientesRow[]> => {
    log('useCliente', 'fetchClientesList', 'Fetching clientes list');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/clientes/carrega_clientes', {
        responseContract: 'Clientes_CarregaClientesResponse',
      });
      log('useCliente', 'fetchClientesList', 'Response', response);

      if (!response.success) {
        throw new Error(response.error || 'Erro ao buscar clientes');
      }
      return response.dados ?? [];
    } catch (err) {
      log('useCliente', 'fetchClientesList', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch origens (sources)
   */
  const fetchOrigens = async () => {
    log('useCliente', 'fetchOrigens', 'Fetching origens');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/config/origens/buscaOrigens', { responseContract: 'Origens_BuscaOrigensResponse' });
      log('useCliente', 'fetchOrigens', 'Response', response);

      if (response.dados) {
        // Convert object to array format
        const output = [];
        for (const [id, name] of Object.entries(response.dados)) {
          output.push({ ID: id, Name: name });
        }
        return output;
      } else {
        throw new Error('Erro ao buscar origens');
      }
    } catch (err) {
      log('useCliente', 'fetchOrigens', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Consulta CEP (Brazil postal code lookup)
   */
  const consultaCep = async (cep: string) => {
    log('useCliente', 'consultaCep', 'Looking up CEP', cep);
    isLoading.value = true;
    error.value = null;

    try {
      // Remove non-digits
      const cleanCep = cep.replace(/\D/g, '');

      if (cleanCep.length !== 8) {
        throw new Error('CEP inválido');
      }

      const response = await httpService.get(`/index.php/consulta_cep/consulta/${cleanCep}`, { responseContract: 'ConsultaCep_ConsultaResponse' });
      log('useCliente', 'consultaCep', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'CEP não encontrado');
      }
    } catch (err) {
      log('useCliente', 'consultaCep', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch daily chart data for Gestao Clientes
   */
  const fetchGraficoDiario = async () => {
    log('useCliente', 'fetchGraficoDiario', 'Fetching daily chart data');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/gestao_clientes/carrega_infos_grafico_diario', { responseContract: 'GestaoClientes_CarregaInfosGraficoDiarioResponse' });
      log('useCliente', 'fetchGraficoDiario', 'Response', response);

      if (response.dados) {
        return response.dados;
      } else {
        throw new Error('Erro ao buscar dados do gráfico diário');
      }
    } catch (err) {
      log('useCliente', 'fetchGraficoDiario', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch summary chart data for Gestao Clientes
   */
  const fetchGraficoResumo = async () => {
    log('useCliente', 'fetchGraficoResumo', 'Fetching summary chart data');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/gestao_clientes/carrega_infos_grafico_resumo', { responseContract: 'GestaoClientes_CarregaInfosGraficoResumoResponse' });
      log('useCliente', 'fetchGraficoResumo', 'Response', response);

      if (response.dados) {
        return response.dados;
      } else {
        throw new Error('Erro ao buscar dados do gráfico resumo');
      }
    } catch (err) {
      log('useCliente', 'fetchGraficoResumo', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch orcamentos sem compromisso chart data (month by month)
   */
  const fetchGraficoOrcamentosSemCompromisso = async () => {
    log('useCliente', 'fetchGraficoOrcamentosSemCompromisso', 'Fetching orcamentos sem compromisso chart');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/gestao_clientes/carrega_infos_grafico_orcamentos_sem_compromisso_mes_a_mes', { responseContract: 'GestaoClientes_CarregaInfosGraficoOrcamentosSemCompromissoMesAMesResponse' });
      log('useCliente', 'fetchGraficoOrcamentosSemCompromisso', 'Response', response);

      if (response.dados) {
        return response.dados;
      } else {
        throw new Error('Erro ao buscar dados do gráfico de orçamentos sem compromisso');
      }
    } catch (err) {
      log('useCliente', 'fetchGraficoOrcamentosSemCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch document configuration for a franchise (cached).
   * Returns allowed document types, default type, and required flag
   * based on the franchise's country (resolved by the backend).
   *
   * @param {number|string} franquiaId - Franchise ID
   * @returns {Promise<Object>} { document_types, default_document_type, document_required, country }
   */
  const fetchDocumentConfig = async (franquiaId: number) => {
    if (!franquiaId) {
      return {
        document_types: ['cpf', 'cnpj'],
        default_document_type: 'cpf',
        document_required: false,
        country: 'Brasil'
      };
    }

    const cacheKey = `documentConfig.${franquiaId}`;

    const result = await cacheService.get(cacheKey, async () => {
      log('useCliente', 'fetchDocumentConfig', 'Fetching from API for franchise', franquiaId);
      const response = await httpService.get(`/index.php/clientes/document_config?franquia_id=${franquiaId}`, {
        responseContract: 'Clientes_DocumentConfigResponse',
        inputContract: 'Clientes_DocumentConfigInput',
      });

      if (response.success) {
        return response.dados;
      }

      throw new Error(response.error || 'Erro ao buscar configuração de documento');
    });

    // Validate cached result — if it has null values, invalidate and refetch
    if (!result || !result.document_types) {
      log('useCliente', 'fetchDocumentConfig', 'Invalid cached data, invalidating', result);
      await cacheService.invalidate(cacheKey);
      const response = await httpService.get(`/index.php/clientes/document_config?franquia_id=${franquiaId}`, {
        responseContract: 'Clientes_DocumentConfigResponse',
        inputContract: 'Clientes_DocumentConfigInput',
      });
      if (response.success) {
        const freshData = response.dados;
        if (freshData && freshData.document_types) {
          return freshData;
        }
      }
      // Fallback
      return { document_types: ['cpf', 'cnpj'], default_document_type: 'cpf', document_required: false, country: 'Brasil' };
    }

    return result;
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchCliente,
    fetchClientesList,
    createCliente,
    updateCliente,
    updateClientePut,
    fetchOrigens,
    consultaCep,
    fetchDocumentConfig,
    fetchGraficoDiario,
    fetchGraficoResumo,
    fetchGraficoOrcamentosSemCompromisso
  };
}
